import {
  LoginSuccess as UnauthorizedLoginSuccess,
  RefreshTokenSuccess as UnauthorizedRefreshTokenSuccess,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/auth/auth_respones_pb';

import { ILoginDeviceInfo } from 'APP/Tasks/authorization/login/login.types';
import {
  ClientConfigKey,
  IAuthUrlSuccess,
  IClientConfigBackground,
  IClientConfigItem,
  IDeviceKeyPackagesCount,
  ILoginByCode,
  ILoginSuccess,
  IRefreshToken,
  IRegistrationUrlSuccess,
  IUnauthorizedLoginSuccess,
  Platform,
} from 'APP/model/auth/authModel.types';
import { keyPackageAlgorithmMap } from 'APP/model/e2ee/e2eeModel.mapper';
import { mapIUser } from 'APP/model/user/userModel.b2b.mapper';
import { mapDeviceStatusResponse } from 'APP/model/userSettings/userSettingsModel.mapper';
import { WsRequest } from 'APP/wsApi/wsAppApi/requests/request.types';
import {
  Background,
  ClientConfigItem,
  LoginDeviceInfo,
} from 'APP/wsApi/wsAppApi/types/auth/auth_model_pb';
import {
  LoginByCodeRequest,
  RefreshTokenRequest,
} from 'APP/wsApi/wsAppApi/types/auth/auth_requests_pb';
import {
  AuthUrlSuccess,
  LoginSuccess,
  RefreshTokenSuccess,
  RegistrationUrlSuccess,
} from 'APP/wsApi/wsAppApi/types/auth/auth_respones_pb';
import { Device_PlatformType } from 'APP/wsApi/wsAppApi/types/device/device_model_pb';
import {
  DeviceKeyPackagesCount,
  KeyPackage_Algorithm,
} from 'APP/wsApi/wsAppApi/types/encryption/encryption_model_pb';

export function mapDevicePlatform(platform: Platform): Device_PlatformType {
  const devicePlatformMap: Record<Platform, Device_PlatformType> = {
    [Platform.WEB]: Device_PlatformType.WEB,
    [Platform.IOS]: Device_PlatformType.IOS,
    [Platform.ANDROID]: Device_PlatformType.ANDROID,
    [Platform.HUAWEI]: Device_PlatformType.HUAWEI,
  };

  return devicePlatformMap[platform] ?? Device_PlatformType.OTHER;
}

export function mapDeviceRequest(deviceInfo: ILoginDeviceInfo): WsRequest<LoginDeviceInfo> {
  return {
    deviceId: deviceInfo.deviceId || '',
    platform: mapDevicePlatform(deviceInfo.platform),
    device: deviceInfo.device || '',
    osName: deviceInfo.osName || '',
    osVersion: deviceInfo.osVersion || '',
    appVersion: deviceInfo.appVersion || '',
  };
}

function mapClientConfigBackground(background: Background): IClientConfigBackground {
  return {
    id: background.id,
    order: background.order,
    url: background.url,
    averageColor: background.averageColor,
  };
}

export function mapIClientConfigItem(item: ClientConfigItem): IClientConfigItem | null {
  switch (item.type.case) {
    case 'userStickersUpdateTs':
      return {
        key: ClientConfigKey.UserStickersLastUpdateTimestamp,
        payload: {
          updateTs: Number(item.type.value.updateTs),
        },
      };
    case 'supportedSDKVersion':
      return {
        key: ClientConfigKey.SupportedSDKVersion,
        payload: item.type.value,
      };
    case 'videoAutoplay':
      return {
        key: ClientConfigKey.VideoAutoplay,
        payload: item.type.value,
      };
    case 'reactionsUpdateTs':
      return {
        key: ClientConfigKey.ReactionsLastUpdateTimestamp,
        payload: {
          updateTs: Number(item.type.value.updateTs),
        },
      };
    case 'stickersUpdateTs':
      return {
        key: ClientConfigKey.StickersLastUpdateTimestamp,
        payload: {
          updateTs: Number(item.type.value.updateTs),
        },
      };
    case 'adminAuthUrlBase':
      return {
        key: ClientConfigKey.AdminAuthUrlBase,
        payload: item.type.value,
      };
    case 'backgrounds':
      return {
        key: ClientConfigKey.Backgrounds,
        payload: {
          backgrounds: item.type.value.items.map(mapClientConfigBackground).sort((a, b) => {
            return a.order - b.order;
          }),
        },
      };
  }

  return null;
}

export function mapLoginByCodeRequest(
  request: ILoginByCode
): WsRequest<Omit<LoginByCodeRequest, 'device'>> & { device: WsRequest<LoginDeviceInfo> } {
  return {
    device: mapDeviceRequest(request.device),
    code: request.code || '',
    redirectUri: request.redirectUri || '',
  };
}

export function mapLoginResponse(response: LoginSuccess): ILoginSuccess {
  return {
    refreshToken: response.refreshToken,
    accessToken: response.accessToken,
    clientConfig: (response.clientConfig?.items || [])
      .map(mapIClientConfigItem)
      .filter(Boolean) as IClientConfigItem[],
    user: response.user ? mapIUser(response.user) : response.user,
    deviceKeyPackagesCount: mapDevicesKeyPackagesCountResponse(response.deviceKeyPackagesCount),
    deviceStatus: mapDeviceStatusResponse(response.deviceStatus),
  };
}

export function mapRefreshTokenRequest(
  request: IRefreshToken
): WsRequest<Omit<RefreshTokenRequest, 'device'>> & { device: WsRequest<LoginDeviceInfo> } {
  return {
    refreshToken: request.refreshToken || '',
    device: mapDeviceRequest(request.device),
  };
}

export function mapUnauthorizedLoginResponse(
  response: UnauthorizedLoginSuccess
): IUnauthorizedLoginSuccess {
  return {
    refreshToken: response.refreshToken,
    accessToken: response.accessToken,
  };
}

export function mapRefreshTokenResponse(response: RefreshTokenSuccess): ILoginSuccess {
  return {
    refreshToken: response.refreshToken,
    accessToken: response.accessToken,
    clientConfig: (response.clientConfig?.items || [])
      .map(mapIClientConfigItem)
      .filter(Boolean) as IClientConfigItem[],
    user: response.user ? mapIUser(response.user) : response.user,
    deviceKeyPackagesCount: mapDevicesKeyPackagesCountResponse(response.deviceKeyPackagesCount),
    deviceStatus: mapDeviceStatusResponse(response.deviceStatus),
  };
}

export function mapUnauthorizedRefreshTokenResponse(
  response: UnauthorizedRefreshTokenSuccess
): IUnauthorizedLoginSuccess {
  return {
    refreshToken: response.refreshToken,
    accessToken: response.accessToken,
  };
}

export function mapGetAuthUrlResponse(response: AuthUrlSuccess): IAuthUrlSuccess {
  return {
    authUrl: response.authUrl,
    supportEmail: response.supportEmail,
  };
}

export function mapGetRegistrationUrlResponse(
  response: RegistrationUrlSuccess
): IRegistrationUrlSuccess {
  return {
    registrationUrl: response.registrationUrl,
    adminUiUrl: response.adminUiUrl,
  };
}

export function mapDevicesKeyPackagesCountResponse(
  keyPackageCount: DeviceKeyPackagesCount[]
): IDeviceKeyPackagesCount | null {
  const curve25519KeyPackagesCount = keyPackageCount.find(
    (item) => item.algorithm === KeyPackage_Algorithm.CURVE_25519
  );

  if (!curve25519KeyPackagesCount) {
    return null;
  }

  return {
    missingCount: curve25519KeyPackagesCount.missingCount,
    availableCount: curve25519KeyPackagesCount.availableCount,
    algorithm: keyPackageAlgorithmMap[curve25519KeyPackagesCount.algorithm],
  };
}
